import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import {
  cancelStudentAdmissionViaAdmin,
  updateStudentDetailsViaAdmin,
  getAllProgramsViaAdmin,
  getAllBatchesViaAdmin,
  resetStudentPasswordViaAdmin,
  updateStudentPhotoAndSignViaAdmin,
} from "../../../utils/Api";
import { toast } from "react-toastify";

const StudentDetailsModal = ({ open, onClose, student, onUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [programs, setPrograms] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [studentPhoto, setStudentPhoto] = useState(null);
  const [studentSign, setStudentSign] = useState(null);


  const getInitialValue = (section, field) => {
    return editedFields[`${section}.${field}`] ?? student[section]?.[field];
  };

  const fetchProgramsAndBatches = useCallback(async () => {
    try {
      const [programsRes, batchesRes] = await Promise.all([
        getAllProgramsViaAdmin(),
        getAllBatchesViaAdmin(),
      ]);
      setPrograms(programsRes.programs);
      setBatches(batchesRes.batches);
      // Initially filter batches based on the student's current program
      const initialFiltered = batchesRes.batches.filter(
        (b) => b.program?.name === student.academicDetails?.program,
      );
      setFilteredBatches(initialFiltered);
    } catch (error) {
      toast.error("Failed to load programs or batches");
    }
  }, [student]);

  useEffect(() => {
    setEditedFields({}); // Clear edited fields when modal opens
    if (isEditMode) {
      fetchProgramsAndBatches();
    }
  }, [student, isEditMode, fetchProgramsAndBatches]);

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (fileType === "photo") {
      setStudentPhoto(file);
    } else if (fileType === "sign") {
      setStudentSign(file);
    }
  };

  const handleInputChange = (e, section, field) => {
    setEditedFields({
      ...editedFields,
      [`${section}.${field}`]: e.target.value,
    });
  };

  const handleProgramChange = (e) => {
    const programName = e.target.value;
    const newFilteredBatches = batches.filter(
      (b) => b.program?.name === programName,
    );
    setFilteredBatches(newFilteredBatches);
    setEditedFields({
      ...editedFields,
      "academicDetails.program": programName,
      "academicDetails.batch": {}, // Reset batch
    });
  };

  const handleBatchChange = (e) => {
    const batchId = e.target.value;
    const selectedBatch = batches.find((b) => b._id === batchId);
    setEditedFields({
      ...editedFields,
      "academicDetails.batch": {
        id: selectedBatch._id,
        name: selectedBatch.batchName,
      },
    });
  };

  const handleSaveChanges = async () => {
    try {
      if (studentPhoto || studentSign) {
        const formData = new FormData();
        if (studentPhoto) {
          formData.append("photo", studentPhoto);
        }
        if (studentSign) {
          formData.append("sign", studentSign);
        }
        await updateStudentPhotoAndSignViaAdmin(student._id, formData);
      }

      if (Object.keys(editedFields).length > 0) {
        await updateStudentDetailsViaAdmin(student._id, editedFields);
      }

      toast.success("Student details updated successfully");
      setIsEditMode(false);
      onUpdate();
      onClose();
    } catch (error) {
      toast.error("Failed to update student details");
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedFields({});
  };

  const handlePasswordAction = async (type) => {
    const confirmAction = window.confirm(
      `Are you sure you want to ${
        type === "register" ? "send registration details" : "reset this student's password"
      }? A new password will be sent to their email address.`,
    );

    if (confirmAction) {
      try {
        const response = await resetStudentPasswordViaAdmin(student._id, null, type);
        toast.success(response.message);
      } catch (error) {
        if (error.response && error.response.status === 400) {
          const manualEmail = prompt(
            "No email found. Please enter an email address to send the details to:",
          );
          if (manualEmail) {
            try {
              const response = await resetStudentPasswordViaAdmin(
                student._id,
                manualEmail,
                type,
              );
              toast.success(response.message);
            } catch (finalError) {
              toast.error("Failed to complete the action.");
            }
          }
        } else {
          toast.error("Failed to complete the action.");
        }
      }
    }
  };

  const handleCancelAdmission = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to cancel this student's admission? This action cannot be undone.",
    );

    console.log(student);

    if (confirmed) {
      try {
        await cancelStudentAdmissionViaAdmin(student._id);
        alert("Student admission cancelled successfully");
        onClose(); // Close the modal after successful cancellation
      } catch (error) {
        console.error("Error cancelling admission:", error);
        alert("Failed to cancel student admission");
      }
    }
  };

  const renderField = (section, field, label) => (
    <tr>
      <td className="p-2 font-semibold">{label}</td>
      <td className="p-2">
        {isEditMode ? (
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={getInitialValue(section, field) || ""}
            onChange={(e) => handleInputChange(e, section, field)}
          />
        ) : (
          `: ${student[section]?.[field]}`
        )}
      </td>
    </tr>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Student Details</DialogTitle>
      <DialogContent>
        <div className="flex flex-col items-center mb-4">
          <div className="flex justify-between w-full mb-4">
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-full mb-2 flex items-center justify-center overflow-hidden">
                {studentPhoto ? (
                  <img
                    src={URL.createObjectURL(studentPhoto)}
                    alt="Student"
                    className="w-full h-full object-cover"
                  />
                ) : student.studentDetails?.studentImage ? (
                  <img
                    src={student.studentDetails.studentImage}
                    alt="Student"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400">No Image</span>
                  </div>
                )}
              </div>
              {isEditMode && (
                <>
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    className="mt-2"
                  >
                    Change Photo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "photo")}
                    />
                  </Button>
                </>
              )}
              <span className="text-gray-500">Image</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-full mb-2 flex items-center justify-center overflow-hidden">
                {studentSign ? (
                  <img
                    src={URL.createObjectURL(studentSign)}
                    alt="Signature"
                    className="w-full h-full object-contain"
                  />
                ) : student.studentDetails?.studentSign ? (
                  <img
                    src={student.studentDetails.studentSign}
                    alt="Signature"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400">No Signature</span>
                  </div>
                )}
              </div>
              {isEditMode && (
                <>
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    className="mt-2"
                  >
                    Change Signature
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "sign")}
                    />
                  </Button>
                </>
              )}
              <span className="text-gray-500">Signature</span>
            </div>
          </div>
          <table className="w-full text-left">
            <tbody>
              {renderField(
                "studentDetails",
                "firstName",
                "First Name",
              )}
              {renderField(
                "studentDetails",
                "lastName",
                "Last Name",
              )}
              {renderField(
                "studentDetails",
                "emailAddress",
                "Email",
              )}
              {renderField(
                "studentDetails",
                "studentMobileNumber",
                "Student Phone",
              )}
              <tr>
                <td className="p-2 font-semibold">Program</td>
                <td className="p-2">
                  {isEditMode ? (
                    <FormControl fullWidth size="small">
                      <InputLabel>Program</InputLabel>
                      <Select
                        value={getInitialValue("academicDetails", "program") || ""}
                        onChange={handleProgramChange}
                        label="Program"
                      >
                        {programs.map((p) => (
                          <MenuItem key={p._id} value={p.programName}>
                            {p.programName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    `: ${student.academicDetails?.program}`
                  )}
                </td>
              </tr>
              <tr>
                <td className="p-2 font-semibold">Batch</td>
                <td className="p-2">
                  {isEditMode ? (
                    <FormControl fullWidth size="small">
                      <InputLabel>Batch</InputLabel>
                      <Select
                        value={getInitialValue("academicDetails", "batch")?.id || ""}
                        onChange={handleBatchChange}
                        label="Batch"
                        disabled={!getInitialValue("academicDetails", "program")}
                      >
                        {filteredBatches.map((b) => (
                          <MenuItem key={b._id} value={b._id}>
                            {b.batchName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    `: ${student.academicDetails?.batch?.name}`
                  )}
                </td>
              </tr>
              {renderField(
                "academicDetails",
                "registerNumber",
                "Register Number",
              )}
              {renderField(
                "academicDetails",
                "rollNumber",
                "Roll Number",
              )}
              <tr>
                <td className="p-2 font-semibold">Status</td>

                <td className="p-2">
                  :{" "}
                  {`${student.status
                    .charAt(0)
                    .toUpperCase()}${student.status.slice(1)}`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </DialogContent>
      <DialogActions>
        {isEditMode ? (
          <>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              onClick={handleSaveChanges}
              color="primary"
              variant="contained"
            >
              Save Changes
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setIsEditMode(true)}>Edit</Button>
            <Button
              onClick={() => handlePasswordAction("register")}
              color="info"
            >
              Send Registration
            </Button>
            <Button
              onClick={() => handlePasswordAction("reset")}
              color="warning"
            >
              Reset Password
            </Button>
            {student.status === "active" && (
              <Button
                onClick={handleCancelAdmission}
                color="error"
                variant="contained"
              >
                Cancel Admission
              </Button>
            )}
            <Button onClick={onClose} color="primary">
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StudentDetailsModal;
